// How long one booking of a service runs — the single source of truth for
// every TypeScript surface (widget, staff app, guest app, admin).
//
// This number is not cosmetic. It sets the time window a booking occupies,
// and that window is what capacity is measured over: create_booking_hold and
// check_capacity_for_slot both sum overlapping bookings across it. Two
// surfaces that disagree about the length disagree about how full the day is,
// so the same nominal slot answers differently depending on which channel the
// guest arrived through.
//
// The bot keeps a hand-copy of this table (supabase/functions/chatbot-respond)
// because edge functions are Deno and cannot import from packages/ — the
// established convention here. Change one, change the other.

/**
 * The built-in length for a service type, used only by services that have
 * never set `duration_minutes`.
 *
 * Kept as a fallback rather than backfilled onto every row, so nothing shifts
 * under a live tenant on deploy — and so a service type nobody has thought
 * about yet still gets a sane window instead of a zero-length one.
 */
export function defaultDurationMinutesFor(
  serviceType: string | null | undefined,
): number {
  switch (serviceType) {
    case "dining":
      return 120;
    case "spa":
      return 90;
    case "tasting":
      return 60;
    case "picnic":
      return 180;
    case "tour":
      return 180;
    case "accommodation":
      return 24 * 60; // one night — overridden by the check-out date picker
    case "events":
      return 120; // overridden by the selected event's starts_at/ends_at
    default:
      return 120;
  }
}

/**
 * How long one booking of THIS service runs.
 *
 * The tenant's own `services.duration_minutes` always wins; the per-type table
 * is the fallback. This is what makes an on-demand service expressible at all
 * — a 45-minute ride cannot be said in a table keyed on service type.
 */
export function bookingDurationMinutes(
  service:
    | { service_type?: string | null; duration_minutes?: number | null }
    | null
    | undefined,
): number {
  const d = service?.duration_minutes;
  if (typeof d === "number" && Number.isFinite(d) && d > 0) return d;
  return defaultDurationMinutesFor(service?.service_type);
}

/**
 * Duration choices to offer a human who is allowed to override the length —
 * the widget's Duration select, the staff app's chips.
 *
 * The service's configured length is always present and always first-class:
 * before this existed the options were a fixed list per service type, so a
 * 45-minute ride had NO option matching its own setting. The picker rendered
 * blank, and any touch of it silently resized the booking to 90 minutes or
 * more — quietly consuming several times the capacity the operator intended.
 */
export function durationOptionsFor(
  service:
    | { service_type?: string | null; duration_minutes?: number | null }
    | null
    | undefined,
  standard: readonly number[],
): number[] {
  const configured = bookingDurationMinutes(service);
  const set = new Set<number>(standard);
  set.add(configured);
  return Array.from(set).sort((a, b) => a - b);
}

/** Mirrors booking_option_minutes: fixed option lengths never apply to stays. */
export function optionDurationMinutes(
  serviceType: string | null | undefined,
  unit: { config?: unknown } | null | undefined,
): number | null {
  if (serviceType === "accommodation") return null;
  const config = unit?.config;
  const value = config && typeof config === "object"
    ? (config as Record<string, unknown>).duration_minutes : null;
  return typeof value === "number" && Number.isInteger(value) && value >= 5 && value <= 1440
    ? value : null;
}

/** Use the stored window returned by the hold; older servers omit these keys. */
export function heldBookingWindow<T extends { starts_at: string; ends_at: string }>(
  requested: T,
  held: { starts_at?: string; ends_at?: string },
): T {
  return { ...requested, starts_at: held.starts_at ?? requested.starts_at,
    ends_at: held.ends_at ?? requested.ends_at };
}
